import { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import Login from './components/Login';
import DashboardLayout from './components/DashboardLayout';
import Whiteboard from './components/Whiteboard';
import Schedule from './components/Schedule';
import WODCalendar from './components/WODCalendar';
import Leaderboard from './components/Leaderboard';
import CoachDashboard from './components/CoachDashboard';
import AthleteDashboard from './components/AthleteDashboard';
import MembershipManagement from './components/MembershipManagement';
import MembershipBlock from './components/MembershipBlock';
import CheckIn from './components/CheckIn';
import ChangePasswordDialog from './components/ChangePasswordDialog';
import AthleteIntro from './components/AthleteIntro';
import BirthdayPrompt from './components/BirthdayPrompt';
import BirthdayPopup from './components/BirthdayPopup';
import MyBirthdayPopup from './components/MyBirthdayPopup';
import HideBadgeOnMobile from './components/HideBadgeOnMobile';
import NotificationSettings from './components/NotificationSettings';
import NotificationAnnouncement from './components/NotificationAnnouncement';
import InvitePage from './components/InvitePage';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import { Toaster } from 'sonner';
import { registerServiceWorker } from './services/notificationService';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const isCoachRole = (role) => role === 'coach' || role === 'head_coach';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [showAthleteIntro, setShowAthleteIntro] = useState(false);
  const [showBirthdayPrompt, setShowBirthdayPrompt] = useState(false);
  const [showBirthdayPopup, setShowBirthdayPopup] = useState(false);
  const [showMyBirthdayPopup, setShowMyBirthdayPopup] = useState(false);
  const [birthdayPeople, setBirthdayPeople] = useState([]);
  const [showNotifAnnouncement, setShowNotifAnnouncement] = useState(false);
  const [isInviteFlow, setIsInviteFlow] = useState(false);
  const [showPWAInstall, setShowPWAInstall] = useState(false);
  const deferredPromptRef = useRef(null);
  const [canInstall, setCanInstall] = useState(false);

  // Show notification announcement once for all users (localStorage-gated)
  useEffect(() => {
    if (
      user && !loading &&
      !showPasswordChange && !showAthleteIntro && !showBirthdayPrompt &&
      !showMyBirthdayPopup && !showBirthdayPopup &&
      !localStorage.getItem('notifications_feature_seen_v1')
    ) {
      const t = setTimeout(() => setShowNotifAnnouncement(true), 800);
      return () => clearTimeout(t);
    }
  }, [user, loading, showPasswordChange, showAthleteIntro, showBirthdayPrompt, showMyBirthdayPopup, showBirthdayPopup]);

  // Global interceptor: auto-logout on 401 from any API call
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 401 && user) {
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
          setUser(null);
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [user]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
    // Register service worker for push notifications
    registerServiceWorker();

    // Pick up the event if it already fired before React mounted
    if (window.__pwaDeferred) {
      deferredPromptRef.current = window.__pwaDeferred;
      window.__pwaDeferred = null;
      setCanInstall(true);
    }

    // Also listen for it firing after React mounts
    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      deferredPromptRef.current = e;
      setCanInstall(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      
      if (response.data.must_change_password) {
        setShowPasswordChange(true);
      } else if (response.data.role === 'athlete' && !response.data.has_seen_intro) {
        setShowAthleteIntro(true);
      } else if (!response.data.birthday) {
        setShowBirthdayPrompt(true);
      } else {
        // Check for today's birthdays
        checkBirthdays(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const cleanupOldBirthdayKeys = () => {
    const today = new Date().toISOString().split('T')[0];
    Object.keys(localStorage)
      .filter(key =>
        (key.startsWith('birthday_shown_') || key.startsWith('my_birthday_shown_')) &&
        !key.endsWith(today)
      )
      .forEach(key => localStorage.removeItem(key));
  };

  const checkBirthdays = async (userData) => {
    cleanupOldBirthdayKeys();
    const today = new Date().toISOString().split('T')[0];

    // Check if today is the logged-in user's own birthday
    const d = new Date();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const todayMMDD = `${mm}-${dd}`;
    const isMyBirthday = userData?.birthday === todayMMDD;
    const myBirthdayKey = `my_birthday_shown_${today}`;
    const showPersonal = isMyBirthday && !localStorage.getItem(myBirthdayKey);

    // Fetch other people's birthdays (not shown yet today)
    const generalKey = `birthday_shown_${today}`;
    let otherPeople = [];
    if (!localStorage.getItem(generalKey)) {
      try {
        const res = await axios.get(`${API}/birthdays/today`);
        otherPeople = (res.data || []).filter(p => p.id !== userData?.id);
        if (otherPeople.length > 0) {
          localStorage.setItem(generalKey, 'true');
        }
      } catch {}
    }

    if (showPersonal) {
      localStorage.setItem(myBirthdayKey, 'true');
      setBirthdayPeople(otherPeople); // stage others for after personal popup
      setShowMyBirthdayPopup(true);
    } else if (otherPeople.length > 0) {
      setBirthdayPeople(otherPeople);
      setShowBirthdayPopup(true);
    }
  };

  const handleLogin = (token, userData, isInvite = false) => {
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
    if (isInvite) setIsInviteFlow(true);
    
    if (userData.must_change_password) {
      setShowPasswordChange(true);
    } else if (userData.role === 'athlete' && !userData.has_seen_intro) {
      setShowAthleteIntro(true);
    } else if (!userData.birthday) {
      setShowBirthdayPrompt(true);
    } else {
      checkBirthdays(userData);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const handlePasswordChanged = async () => {
    const wasInviteFlow = isInviteFlow;
    setShowPasswordChange(false);
    setIsInviteFlow(false);

    if (wasInviteFlow) {
      setShowPWAInstall(true);
    } else {
      await fetchCurrentUser();
    }
  };

  const handlePWAInstallDone = async () => {
    setShowPWAInstall(false);
    await fetchCurrentUser();
  };

  const triggerInstall = async () => {
    if (!deferredPromptRef.current) return false;
    deferredPromptRef.current.prompt();
    const { outcome } = await deferredPromptRef.current.userChoice;
    deferredPromptRef.current = null;
    setCanInstall(false);
    return outcome === 'accepted';
  };

  const handleIntroComplete = async () => {
    setShowAthleteIntro(false);
    await fetchCurrentUser();
  };

  const handleBirthdaySet = async () => {
    setShowBirthdayPrompt(false);
    await fetchCurrentUser();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="text-[#CCFF00] text-2xl font-heading uppercase tracking-wider">Loading...</div>
      </div>
    );
  }

  if (!user) {
    // Handle invite magic link before showing login
    if (window.location.pathname.startsWith('/invite')) {
      return (
        <>
          <InvitePage onLogin={handleLogin} />
          <Toaster position="top-right" theme="dark" />
        </>
      );
    }
    return (
      <>
        <Login onLogin={handleLogin} />
        <Toaster position="top-right" theme="dark" />
      </>
    );
  }

  // Membership block for inactive members (only head_coach is exempt)
  if (user.membership_active === false && user.role !== 'head_coach') {
    return (
      <>
        <MembershipBlock onLogout={handleLogout} />
        <Toaster position="top-right" theme="dark" />
      </>
    );
  }

  return (
    <BrowserRouter>
      <HideBadgeOnMobile />
      <div className="App">
        {user && showPWAInstall && (
          <PWAInstallPrompt
            open={showPWAInstall}
            onDismiss={handlePWAInstallDone}
            onTriggerInstall={triggerInstall}
            canInstall={canInstall}
          />
        )}

        {user && showPasswordChange && (
          <ChangePasswordDialog 
            open={showPasswordChange} 
            onPasswordChanged={handlePasswordChanged}
            isInviteFlow={isInviteFlow}
          />
        )}
        
        {user && showAthleteIntro && (
          <AthleteIntro 
            open={showAthleteIntro} 
            onComplete={handleIntroComplete}
          />
        )}

        {user && showBirthdayPrompt && (
          <BirthdayPrompt
            open={showBirthdayPrompt}
            onComplete={handleBirthdaySet}
          />
        )}

        {user && showMyBirthdayPopup && (
          <MyBirthdayPopup
            open={showMyBirthdayPopup}
            user={user}
            onDismiss={() => {
              setShowMyBirthdayPopup(false);
              if (birthdayPeople.length > 0) setShowBirthdayPopup(true);
            }}
          />
        )}

        {user && showBirthdayPopup && (
          <BirthdayPopup
            open={showBirthdayPopup}
            onDismiss={() => setShowBirthdayPopup(false)}
            birthdayPeople={birthdayPeople}
          />
        )}

        {user && showNotifAnnouncement && (
          <NotificationAnnouncement
            open={showNotifAnnouncement}
            onDismiss={() => setShowNotifAnnouncement(false)}
          />
        )}
        
        <Routes>
          <Route path="/" element={<DashboardLayout user={user} onLogout={handleLogout} />}>
            <Route index element={<Whiteboard user={user} />} />
            <Route path="whiteboard" element={<Whiteboard user={user} />} />
            <Route path="schedule" element={<Schedule user={user} />} />
            <Route path="wod-calendar" element={<WODCalendar user={user} />} />
            <Route path="leaderboard" element={<Leaderboard user={user} />} />
            {isCoachRole(user.role) && (
              <Route path="coach-dashboard" element={<CoachDashboard user={user} />} />
            )}
            {user.role === 'head_coach' && (
              <Route path="membership" element={<MembershipManagement user={user} />} />
            )}
            {user.role === 'athlete' && (
              <Route path="athlete-dashboard" element={<AthleteDashboard user={user} />} />
            )}
            <Route path="checkin" element={<CheckIn user={user} />} />
            <Route path="notifications" element={<NotificationSettings />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" theme="dark" />
      </div>
    </BrowserRouter>
  );
}

export default App;
